/**
 * Types for the v3 final report.
 *
 * v2 valued each account at the best of four sources: the "original
 * reconciliation" held in D1, what we synced from operator backends, manual
 * backend checks, and Raketech's July 2026 workbook.
 *
 * The first of those was wrong. The figures in D1 were imported from the
 * January 2025 correction file, which Raketech disowned in writing on 15 August
 * 2025 and again on 14 May 2026. Their June 2025 workbook carries that column
 * verbatim as "New earnings 1st file (EUR)" next to the column they treat as
 * authoritative, "Adwin earnings".
 *
 * v3 therefore replaces that source with the June workbook's own credited
 * column, and keeps both the January column and Raketech's "Reported (EUR)"
 * column as references that are always shown and never claimed on.
 */

import type { NewReconOffer } from './raketechReconDiff.types';

/** Where an account's earnings figure came from, in ascending order of how
 *  hard it is for Raketech to argue with. */
export type ClaimSource =
  | 'june_recon'
  | 'june_reported'
  | 'synced'
  | 'manual_check'
  | 'raketech_restated';

export const CLAIM_SOURCE_LABELS: Record<ClaimSource, string> = {
  june_recon: 'June 2025 reconciliation',
  june_reported: 'Their monthly reports',
  synced: 'Synced from operator',
  manual_check: 'Manual backend check',
  raketech_restated: 'Raketech restated',
};

/** One candidate earnings figure for an account, before the best is picked. */
export interface SourceCandidate {
  source: ClaimSource;
  /** EUR, already on our net-of-margin basis. */
  amount: number;
  /** Present when the source figure was in another currency. */
  originalAmount?: number;
  originalCurrency?: string;
  /** True when a 5% partner margin was applied to reach `amount`. */
  marginApplied: boolean;
  available: boolean;
  /** Set when a position Adwin has already taken in correspondence rules the
   *  source out, so the figure is still shown but cannot value the account. */
  withheld?: string;
}

/**
 * Figures carried for context only. None of these can win an account.
 *
 * They exist so the report can show, on the same line, what Raketech credited,
 * what Raketech reported and paid at the time, and what the disowned January
 * file said — which is the difference between a claim and an assertion.
 */
export interface ClaimReference {
  /** "New earnings 1st file (EUR)": the January 2025 file, EUR. */
  januaryFile: number;
  /** What adwstats previously used for this account, from D1. */
  previouslyClaimed: number;
  /** "Reported (EUR)": what Raketech's monthly reports paid out on. */
  raketechReported: number;
  /** Reported less credited, from their own Difference column. Negative means
   *  Raketech's own file says the account earned more than it reported. */
  juneDifference: number;
  /** Raketech's own annotations against the account in the June workbook. */
  juneComments: string[];
  /** v4: the "Adwin earnings" column of Raketech's July 2026 report for the
   *  account, all months. Their account-level column takes the 5% in period 2
   *  as well, where their own Summary sheet takes none; the claim follows the
   *  Summary, so this is carried to show the difference. */
  julyStatedAdwinEarnings?: number;
}

export interface ClaimRow {
  key: string;
  /** Raketech's account id. Equals `HUB ID` in the new workbook and `ID` in the
   *  June workbook. */
  accountId: string;
  acIds: string[];
  username: string;
  partner: string;
  brands: string[];
  geos: string[];
  currency: string;

  /** True when this account exists only in the new workbook. */
  newToThisReport: boolean;
  /** True when we hold no adwstats account for it and cannot verify independently. */
  missingFromAdwstats: boolean;
  /** True when the June workbook carries this account at all. */
  inJuneRecon: boolean;

  invoicedRecon: number;
  invoicedPostRecon: number;
  invoicedTotal: number;

  candidates: SourceCandidate[];
  reference: ClaimReference;
  /**
   * What Raketech's monthly reports paid on for this account. On
   * `acquisition_only` the deal bought acquisitions and no revenue share, so
   * operator data is not a measure of what was owed and is not used to value
   * the row; the open question there is the count of acquisitions reported.
   */
  dealBasis: 'acquisition_only' | 'revenue_share' | 'mixed' | 'unknown';
  /** Why a source was ruled out by Adwin's own position in correspondence. */
  concession?: string;
  /** The winning candidate over the reconciled period. */
  chosenSource: ClaimSource;
  earnedReconPeriod: number;
  /** Dec 2024 onward, from Raketech's own monthly columns. */
  earnedTail: number;
  /** What v1 would have used for the tail, when an AC session supplied it. */
  earnedTailFromAc: number;
  earnedTotal: number;

  /** Negative means underpaid: invoiced less than earned. */
  underpaid: number;
  underpaidPct: number;

  /** How much of the shortfall exists only because of their restatement. */
  attributableToRestatement: number;
  /** How much of the earnings figure rests on Raketech's own "Reported (EUR)"
   *  column standing above what they later credited. This is the part of the
   *  account they wrote down after Adwin had already invoiced their report. */
  reportedLift: number;
  /**
   * The reduction inside Raketech's own June 2025 file: what its monthly
   * reports paid out on, less what the same file credits the account with.
   * A fact about their document, so an account written down to nil stays a
   * written-down account even where Adwin values it from another source.
   */
  reportedVsCredited: number;
  /**
   * v4: months after November 2024 in which Adwin invoiced, on Raketech's own
   * monthly report, more than Raketech's July 2026 report now carries — the
   * same write-down as `reportedVsCredited`, on the later months. Adwin
   * invoices net of the 5% and the report is gross from May 2025, so the
   * ordinary case runs the other way and is not counted.
   */
  tailWrittenDown: number;
  /** How much this account's shortfall moved by dropping the January column.
   *  Positive means v2 overstated it. */
  januaryColumnEffect: number;

  offers: NewReconOffer[];
  monthly: ClaimMonth[];
  coverage: CoverageDiagnostics;
  /** Raketech's own invoice lines for this account, for the drilldown. */
  invoiceEntries: ClaimInvoiceEntry[];
}

/** One invoice line Raketech raised, as `/api/raketech/compare` matched it. */
/**
 * What the evidence does not cover.
 *
 * An account is only judged on the months both sides describe. Where Adwin
 * invoiced and Raketech's files say nothing, the account reads as an
 * overpayment when in truth nothing is known about the month. These figures
 * make that visible instead of letting it settle silently into the total.
 */
export interface CoverageDiagnostics {
  /** Months Adwin invoiced where no source of theirs reports anything. */
  gapMonths: number;
  gapInvoiced: number;
  /** Of `gapInvoiced`, the part where the same brand IS in their files that
   *  month under a different account id, so the earnings exist but sit
   *  elsewhere. This is a mapping difference, not missing data. */
  gapReportedElsewhere: number;
  /** Of `gapInvoiced`, the part where the brand is absent from their files for
   *  that month altogether. This is missing data. */
  gapAbsent: number;
  /** Months reported at less than half of what was invoiced. */
  partialMonths: number;
  partialWedge: number;
  /** Earnings if the best source is chosen month by month rather than once for
   *  the whole account, which stops a source that is silent in a month from
   *  suppressing a source that is not. Floored at the account's own figure. */
  monthLevelEarned: number;
  /** monthLevelEarned less earnedTotal. Never negative. */
  monthLevelLift: number;

  /** The apparent overpayment, taken apart. An account reads as overpaid when
   *  Adwin invoiced more than their files report, but that is only an
   *  overpayment where their files actually describe the month. */
  blankMonthOverpaid: number;
  partialMonthOverpaid: number;
  /** The part their own figures do describe, and which is therefore a real
   *  counter-exposure rather than an artefact of what they left out. */
  evidencedOverpaid: number;

  /** Invoicing that falls in a month their own figures describe. This is the
   *  only invoicing a like-for-like comparison deducts: an invoice raised for a
   *  month they never reported says nothing about a month they did. */
  describedInvoiced: number;
  /** invoicedTotal less describedInvoiced. Set aside rather than claimed: their
   *  reporting has to cover the month before either side can be measured. */
  undescribedInvoiced: number;
  /** `describedInvoiced` decided across the partner group rather than the single
   *  account. Raketech books one month's earnings under a sibling account often
   *  enough that setting the invoicing aside per account would drop the invoice
   *  while keeping the sibling's earnings, and overstate the claim. */
  describedInvoicedInGroup: number;
}

/**
 * The same shortfall at three scopes.
 *
 * `net` lets an account Raketech overpaid discharge one it underpaid. That is
 * the conservative reading and the one to open with. The other two do not,
 * because a debt on one account is not answered by a credit on another; they
 * differ only in whether an account Raketech re-mapped between ids is put back
 * together first, which it should be, or left split, which overstates.
 */
export interface ClaimScopeTotals {
  net: number;
  /** Net, except that an overpayment only nets to the extent their own file
   *  actually reports the months it rests on. A blank month of theirs is not
   *  evidence that Adwin was overpaid, so it is not allowed to pay for a
   *  shortfall somewhere else. */
  netEvidenced: number;
  byPartner: number;
  /** Both corrections at once, and the position this report is built to
   *  defend: accounts grouped by partner so an account they re-mapped between
   *  ids cannot be counted twice, and within the group an overpayment offsets a
   *  shortfall only as far as their own file reports the months it rests on. */
  byPartnerEvidenced: number;
  byAccount: number;
}

/**
 * The claim at three levels of assumption, so the headline is never the only
 * number on the table and the distance to the others is explicit.
 */
export interface ClaimBand {
  /** Each account judged on its single best source. Blanks read as zero. */
  stated: ClaimScopeTotals;
  /** Each month judged on whichever source covers it. Adds only months a
   *  source of theirs does describe, so nothing here is an estimate. */
  monthLevel: ClaimScopeTotals;
  /**
   * Like for like: only the months their own figures describe are compared, on
   * both sides. A month they left blank is taken out of the comparison rather
   * than read as zero earnings, and the invoicing Adwin raised for that month
   * is set aside with it. This is the basis the report defends, because it is
   * the only one that treats their silence the same way in both directions.
   */
  likeForLike: ClaimScopeTotals;
  /** Invoicing set aside by the like-for-like basis: raised by Adwin in months
   *  no source of theirs describes. Not claimed, and not credited to them
   *  either, until they report the month. */
  undescribedInvoiced: number;
  /** Invoiced months no source of theirs describes at all, and months they
   *  describe at under half. Not claimed: it is what their mapping table and
   *  the missing account data would have to resolve. */
  unresolvedCoverage: number;
  /** Of `unresolvedCoverage`, the part where the brand IS in their files that
   *  month under another id, so it is a mapping question, not missing data. */
  unresolvedRemapping: number;
  /** Of `unresolvedCoverage`, the part sitting on accounts already short. It
   *  depresses a shortfall that is otherwise proved, rather than propping up an
   *  overpayment. */
  unresolvedOnShortfallAccounts: number;
}

/** One invoice line Raketech raised, as `/api/raketech/compare` matched it. */
export interface ClaimInvoiceEntry {
  label: string;
  ftds: number;
  revenue: number;
  monthly: { month: string; ftds: number; revenue: number }[];
  /** v4: how the attribution placed these lines here, strongest rule first. */
  rules?: string[];
  /** v4: lines in this entry whose account was decided on size, not on the line. */
  judged?: number;
  /** v4: lines in this entry apportioned across siblings in Raketech's reported proportions. */
  split?: number;
  /** v4: other accounts that were candidates for lines in this entry. */
  contested?: string[];
}

/** v4: the attribution's own accounting, carried so the report can show it. */
export interface ClaimAttributionSummary {
  lines: number;
  all: number;
  attributed: number;
  unattributed: number;
  balanced: boolean;
  unattributedLines: number;
  judgedLines: number;
  judgedTotal: number;
  /** Lines apportioned across sibling accounts in Raketech's own reported proportions. */
  splitLines: number;
  splitTotal: number;
  rules: Record<string, { lines: number; revenue: number }>;
}

/**
 * One month of an account, carrying every figure we hold for it.
 *
 * `earned` is the only field that feeds the claim. The rest are shown side by
 * side so the drilldown is a complete picture of the evidence rather than just
 * the number that won.
 */
export interface ClaimMonth {
  month: string;
  invoiced: number;
  ftds: number;
  /** Best evidenced earnings for the month. This is what the claim uses. */
  earned: number;

  /** June 2025 workbook, "Adwin earnings" basis. Feeds the claim. */
  juneRecon: number;
  /** June 2025 workbook, "Reported (EUR)" basis: what their monthly reports paid
   *  out on. Their credited shape scaled to that total. Feeds the claim where it
   *  stands above what they credited. */
  juneReported: number;
  /** View only: the disowned January file's figure for the month. */
  januaryFile: number;
  /** View only: what we synced from the operator, net of the 5% margin. */
  synced: number;
  /** View only: Affiliation Cloud's reported commission. */
  ac: number;
  /** Raketech's restated figure from the July 2026 workbook. */
  restated: number;

  underpaid: number;
  /** True when nothing supports the month on either side, so it cannot be judged. */
  unsupported: boolean;
}

export interface ClaimSummary {
  reconMonths: string[];
  tailMonths: string[];

  accounts: number;
  accountsNewToReport: number;
  accountsMissingFromAdwstats: number;
  accountsInJuneRecon: number;

  invoicedTotal: number;
  earnedTotal: number;
  /** Net of the two below. Reported for continuity with v2, but the claim is
   *  `underpaidGross`: an account Raketech overpaid does not discharge a
   *  different account it underpaid. */
  underpaidTotal: number;
  /** Sum of the accounts invoiced below what they earned. */
  underpaidGross: number;
  /** Sum of the accounts invoiced above what they earned, which is Raketech's
   *  counter-exposure and is stated rather than netted away. */
  overpaidGross: number;
  underpaidAccounts: number;
  overpaidAccounts: number;

  /** Same computation with the restated source withheld, for the bridge. */
  underpaidWithoutRestatement: number;
  restatementEffect: number;
  /** What the same claim came to while the January column was still in it. */
  underpaidOnJanuaryColumn: number;
  januaryColumnEffect: number;

  /** v4: Raketech's own "Adwin earnings" column in the July 2026 report, across
   *  the rows, against the basis the claim values them on. Their Summary sheet
   *  takes no 5% after April 2025; their account column does, on most accounts;
   *  the difference is what that column takes and the claim does not concede. */
  julyStatedColumn?: { theirs: number; claimBasis: number; difference: number; accountsMargined: number };

  bySource: Record<ClaimSource, { accounts: number; earned: number; underpaid: number }>;

  /** Accounts we could not verify independently, and their share of the claim. */
  unverifiableEarnings: number;
  unverifiableUnderpaid: number;

  /** Everything the evidence does not cover, summed across accounts. */
  coverage: CoverageDiagnostics & {
    accountsWithGaps: number;
    /** Gap invoicing sitting on accounts the report currently calls overpaid,
     *  which is the part of `overpaidGross` that is an artefact. */
    gapOnOverpaidAccounts: number;
    /** Gap invoicing sitting on accounts already underpaid, where it depresses
     *  a shortfall that is otherwise proven. */
    gapOnUnderpaidAccounts: number;
  };

  /** Gross figures re-cut so that Raketech moving an account between ids can
   *  neither create an overpayment nor duplicate a shortfall. The net is
   *  identical on all three groupings; only the gross moves. */
  grouped: {
    byBrand: { underpaidGross: number; overpaidGross: number; groups: number };
    byPartner: { underpaidGross: number; overpaidGross: number; groups: number };
  };

  /** The shortfall stated three ways. See `ClaimBand`. */
  band: ClaimBand;

  /** The last month any invoice line detail has been imported for. */
  invoiceDataThrough: string;
  /** Earnings the claim carries in months past `invoiceDataThrough`. Nothing is
   *  deducted against them because no invoice detail exists yet, so they read
   *  as a pure shortfall — and the invoice covering those months is claimed
   *  separately as unpaid. Held here so the two cannot be claimed at once. */
  earningsBeyondInvoiceData: number;
  /** Invoice revenue the comparison attached to more than one account, because
   *  the invoice detail carries a brand and a deal label rather than Raketech's
   *  account id, and which has been taken out of every account but the one whose
   *  own offer is the deal it names. Left in, it would suppress a shortfall
   *  where it lands and inflate the invoiced side of the tie-out against what
   *  Adwin actually billed. */
  duplicateInvoiceRevenueRemoved: number;
  /** What is still duplicated after that. Zero unless a line escapes the match,
   *  and reported so the invoiced side can be shown to be clean. */
  duplicateInvoiceRevenueRemaining: number;
  /** Of `invoicedTotal`, the part falling in months their own figures describe.
   *  This is what the like-for-like basis deducts, and so what that basis has
   *  to be tied out against Adwin's actual billing. */
  describedInvoicedTotal: number;
  /** Of the claim, the part resting on Raketech's own "Reported (EUR)" column
   *  standing above what they later credited: earnings they reported, Adwin
   *  invoiced against, and they then wrote down. */
  reportedWriteDown: number;
  /** Accounts carrying such a write-down, and the part of them their own
   *  comment column annotates. */
  reportedWriteDownAccounts: number;
  reportedWriteDownCommented: number;

  /** v4 only. */
  attribution?: ClaimAttributionSummary;
}

/** One line of the statement of what is actually outstanding. */
export interface OutstandingLine {
  label: string;
  amount: number;
  kind: 'claim' | 'credit' | 'entitlement' | 'subtotal' | 'invoice' | 'total';
  note?: string;
  /** Set when the line depends on a question that is not yet settled. */
  provisional?: boolean;
}

/**
 * The whole position in one column: the commission shortfall, the amounts
 * already settled that must come off it, the separate contractual entitlements
 * that must not be netted against it, and the invoices simply unpaid.
 */
export interface OutstandingStatement {
  lines: OutstandingLine[];
  /** The shortfall on Raketech's figures, less what has been received against it. */
  commissionClaim: number;
  /** v4: commission Raketech's monthly reports recorded as earned that Adwin
   *  never billed in full — its own head, beside the shortfall, on the same
   *  footing as the flat fee. */
  neverBilled: number;
  entitlements: number;
  /** v4: the transaction cost Raketech undertook on 9 May 2025 to compensate,
   *  less the part already delivered or claimed through the 5% it stopped
   *  taking from May 2025. Its own head, on the same footing as the flat fee. */
  transactionCost: number;
  unpaidInvoices: number;
  total: number;
}

/** One line reconciling Raketech's stated position to this report's claim. */
export interface ClaimBridgeStep {
  label: string;
  amount: number | null;
  kind: 'figure' | 'subtotal' | 'addition' | 'deduction' | 'result';
  note?: string;
  /** Set when Raketech themselves state the figure, rather than us deriving it. */
  theirFigure?: boolean;
}
